import React, { useEffect, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import UpdateIcon from '@mui/icons-material/Update';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import BugReportIcon from '@mui/icons-material/BugReport';

const TABS = [
  { id: 'recentlyAdded', label: 'Recently Added', icon: <NewReleasesIcon fontSize="small" /> },
  { id: 'comingSoon', label: 'Coming Soon', icon: <UpdateIcon fontSize="small" /> },
  { id: 'catalogue', label: 'Catalogue', icon: <VideoLibraryIcon fontSize="small" /> },
  { id: 'requests', label: 'Requests', icon: <PlaylistAddIcon fontSize="small" /> },
  { id: 'reportIssues', label: 'Report Issues', icon: <BugReportIcon fontSize="small" /> },
  { id: 'watch', label: 'Watch', icon: <OpenInNewIcon fontSize="small" /> },
];

const ALL_TAB_IDS = [...TABS.map(t => t.id), 'admin'];
const TEXT_TAB_MIN_WIDTH = 140;
const ICON_TAB_MIN_WIDTH = 56;

function NavBar({ activeTab, onTabChange, isAdmin, user, onSignOut }) {
  const tabsAreaRef = useRef(null);
  const [tabsAreaWidth, setTabsAreaWidth] = useState(0);
  const tabValue = ALL_TAB_IDS.includes(activeTab) ? activeTab : false;
  const tabsCount = TABS.length + (isAdmin ? 1 : 0);
  const iconOnlyTabs = tabsAreaWidth > 0 && tabsAreaWidth < tabsCount * TEXT_TAB_MIN_WIDTH;
  const tabMinWidth = iconOnlyTabs ? ICON_TAB_MIN_WIDTH : TEXT_TAB_MIN_WIDTH;

  useEffect(() => {
    const tabsAreaElement = tabsAreaRef.current;
    if (!tabsAreaElement) {
      return undefined;
    }

    const updateTabsAreaWidth = () => {
      setTabsAreaWidth(tabsAreaElement.clientWidth);
    };

    updateTabsAreaWidth();

    const resizeObserver = new ResizeObserver(updateTabsAreaWidth);
    resizeObserver.observe(tabsAreaElement);
    window.addEventListener('resize', updateTabsAreaWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateTabsAreaWidth);
    };
  }, []);

  return (
    <AppBar position="sticky" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }} elevation={0}>
      <Toolbar sx={{ gap: 1, px: { xs: 1, sm: 2 } }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2, flexShrink: 0 }}>
          <Typography
            variant="subtitle1"
            onClick={() => onTabChange('home')}
            sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap', cursor: 'pointer' }}
          >
            DECADE ON DEMAND
          </Typography>
        </Box>

        {/* Tabs */}
        <Box ref={tabsAreaRef} sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => {
              if (v === 'watch') {
                window.open('https://app.plex.tv/', '_blank', 'noopener,noreferrer');
                return;
              }
              onTabChange(v);
            }}
            textColor="primary"
            indicatorColor="primary"
            sx={{ minHeight: 64 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map(tab => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={iconOnlyTabs ? '' : tab.label}
                icon={iconOnlyTabs ? <Tooltip title={tab.label} enterDelay={300}>{tab.icon}</Tooltip> : tab.icon}
                iconPosition="start"
                aria-label={tab.label}
                sx={{ minHeight: 64, minWidth: tabMinWidth }}
              />
            ))}
            {isAdmin && (
              <Tab
                value="admin"
                label={iconOnlyTabs ? '' : 'Admin'}
                icon={iconOnlyTabs ? <Tooltip title="Admin" enterDelay={300}><SettingsIcon fontSize="small" /></Tooltip> : <SettingsIcon fontSize="small" />}
                iconPosition="start"
                aria-label="Admin"
                sx={{ minHeight: 64, minWidth: tabMinWidth, color: 'warning.main' }}
              />
            )}
          </Tabs>
        </Box>

        {/* User + Sign Out */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1, flexShrink: 0 }}>
          {user?.photoURL && (
            <Tooltip title={user.displayName || user.email}>
              <Avatar src={user.photoURL} alt={user.displayName || 'User'} sx={{ width: 32, height: 32 }} />
            </Tooltip>
          )}
          <Button variant="outlined" color="inherit" size="small" onClick={onSignOut} sx={{ whiteSpace: 'nowrap' }}>
            Sign Out
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default NavBar;
